import { z } from 'zod';
import { phoneSchema } from '../api';
import { REGION_CODES, TIMEZONES } from '../regions';
import { cnicSchema } from './patients';
import { usernameSchema } from './org';
import { ADD_ON_MODULES, MODULE_CODES } from '../modules';
import { ISOLATION_MODE, PLATFORM_STAFF_ROLE, ENTITLEMENT_TOGGLE, } from '../enums';
const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(40)
    .regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, 'Use lowercase letters, digits and hyphens');
export const planLimitsSchema = z.object({
    users: z.number().int().min(1).nullable().default(null),
    beds: z.number().int().min(0).nullable().default(null),
    branches: z.number().int().min(1).nullable().default(null),
    storageMb: z.number().int().min(0).nullable().default(null),
    smsCredits: z.number().int().min(0).nullable().default(null),
});
export const createTenantSchema = z.object({
    name: z.string().trim().min(2).max(160),
    /**
     * The subdomain. Optional now: leave it out and the backend derives a
     * unique one from the hospital name (`Aziz Hospital` → `aziz-hospital`,
     * then `-2`, `-3`… if taken). Supply it only to override that.
     */
    slug: slugSchema.optional(),
    planId: z.string().uuid().optional(),
    planIds: z.array(z.string().uuid()).min(1).max(4).optional(),
    addOns: z.array(z.enum(ADD_ON_MODULES)).max(2).default([]),
    isolationMode: z.enum(ISOLATION_MODE).default('shared_row'),
    /**
     * Where the hospital is — a REGIONS code such as `PK` or `US-ET`. The server
     * derives the timezone from it. Preferred over `timezone`; the console
     * sends only this.
     */
    country: z.enum(REGION_CODES).optional(),
    /**
     * Accepted for programmatic callers, but only a zone from the catalogue —
     * a free-text zone that Intl cannot resolve would break every date query
     * for the hospital. Ignored when `country` is given.
     */
    timezone: z.enum(TIMEZONES).default('Asia/Karachi'),
    locale: z.string().min(2).max(10).default('en'),
    /** Defaults to the country's currency; only programmatic callers set it. */
    currency: z.string().length(3).optional(),
    customDomain: z.string().trim().max(190).optional(),
    priceMinor: z.number().int().min(0).default(0),
    billingCycle: z.enum(['monthly', 'annual']).default('monthly'),
    setupFeeMinor: z.number().int().min(0).default(0),
    limits: planLimitsSchema.default({ users: null, beds: null, branches: null, storageMb: null, smsCredits: null }),
    /** Super Admin invited during provisioning. */
    adminUsername: usernameSchema,
    adminEmail: z.string().email().optional(),
    adminName: z.string().trim().min(2).max(120),
    adminPhone: z.string().trim().max(20).optional(),
    /** Seed demo data (departments, charge list, drugs) into the new tenant. */
    seedDemoData: z.boolean().default(true),
    seedDemoHospital: z.boolean().default(false),
});
export const facilityBillingSchema = z.object({
    priceMinor: z.number().int().min(0).default(0),
    billingCycle: z.enum(['monthly', 'annual']).default('monthly'),
    setupFeeMinor: z.number().int().min(0).default(0),
    limits: planLimitsSchema,
});
export const upsertPlanSchema = z.object({
    name: z.string().trim().min(2).max(120),
    code: z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9_]+$/),
    description: z.string().max(500).optional(),
    trialDays: z.number().int().min(0).max(365).default(14),
    moduleFlags: z.array(z.enum(MODULE_CODES)).default(['core']),
    facilityType: z.enum(['hospital', 'lab', 'pharmacy']).optional(),
    isActive: z.boolean().default(true),
});
export const setEntitlementSchema = z.object({
    moduleCode: z.enum(MODULE_CODES),
    status: z.enum(ENTITLEMENT_TOGGLE),
    reason: z.string().max(300).optional(),
});
export const bulkEntitlementSchema = z.object({
    entitlements: z.array(setEntitlementSchema).min(1).max(MODULE_CODES.length),
});
export const createPlatformUserSchema = z.object({
    name: z.string().trim().min(2).max(120),
    fatherName: z.string().trim().min(2).max(120).optional(),
    cnic: cnicSchema.optional(),
    email: z.string().email(),
    phone: phoneSchema.optional(),
    role: z.enum(PLATFORM_STAFF_ROLE),
    password: z.string().min(12).max(200),
});
/** Everything except the password, which has its own reset path. */
export const updatePlatformUserSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    fatherName: z.string().trim().min(2).max(120).nullable().optional(),
    cnic: cnicSchema.nullable().optional(),
    phone: phoneSchema.nullable().optional(),
    role: z.enum(PLATFORM_STAFF_ROLE).optional(),
    isActive: z.boolean().optional(),
});
export const resetPlatformUserPasswordSchema = z.object({
    password: z.string().min(12).max(200),
});
//# sourceMappingURL=platform.js.map